import { Device } from '../api/types/device/device-details/device-details-ok-response-type';
import { PromiseType } from './general';
import * as DeviceManager from '../data/domain/device-manager';
import { Data } from './data-providers';

export enum DeviceVendor {
    Wellaware = 'wellaware',
    Sierra = 'sierra',
    Insulgard = 'insulgard',
    SierraWireless = 'sierra wireless',
    EatonCorporation = 'eaton corporation',
    Eaton = 'eaton',
    SFStarfishGateway = 'BLG_Brightlayer_Gateway',
}

export enum DeviceTab {
    sensorTrend = 'sensorTrend',
    phaseResolvedGraph = 'phaseResolved',
}
export enum SubscriptionStatus {
    Active = 1,
    Expired = 0,
}
export type INStrendData = Data<
    Array<{
        id: number;
        channelName: string;
        deviceId: string;
        deviceName: string;
        value: Array<{
            dateTime: string;
            value: string;
            isNewAlarm: boolean;
            alarmCount: number;
        }>;
        unit: string | null;
    }>
>;

export type RelativeConditionIndex = {
    deviceId: string | null;
    deviceName: string | null;
    data: Array<{
        rank: string | null;
        rankColor: string | null;
        monthAndYear: string | null;
        rankPercentage: string | null;
    }>;
};

export type Uart = {
    baud: string;
    bits: number;
    flow: string;
    pair: string;
    stop: number;
    std: string;
    term: string;
};

export type GatewayDeviceConfig = {
    name: string;
    model: string;
    id: string;
    profile: string;
    serial: string;
    imei: string;
    ethernet: {
        ipaddr: string;
        netmask: number;
    };
    uart1: Uart;
    isRebootRequired: true;
    operation: string;
    configTransactionId: string;
};

export type ConfigTree = {
    gatewayDevice: GatewayDeviceConfig;
    childDevices: [];
};

export type Modbusaddr = {
    ipaddr: string;
    ipport: number;
    unitid: number;
};

export type ChildDeviceType = {
    name: string;
    model: string;
    id: string;
    profile: string;
    modbusaddr: Modbusaddr;
};
export type PhaseResolvedGraphData = {
    deviceId: string | null;
    deviceName: string | null;
    channelNumber: number | null;
    channelName: string | null;
    phaseConnection: string | null;
    chartData: Array<{
        xAxis: string | null;
        yAxis: string | null;
        value: string | null;
    }>;
};

export type TemperatureGraphData = {
    deviceId: string | null;
    deviceName: string | null;
    startDate: string | null;
    scaleX: string | null;
    scaleY: string | null;
    endDate: string | null;
    chartData: Array<{
        dateTime: string | null;
        minTemp: string | null;
        maxTemp: string | null;
    }>;
};

export type HumidityGraphData = {
    deviceId: string | null;
    deviceName: string | null;
    startDate: string | null;
    scaleX: string | null;
    scaleY: string | null;
    endDate: string | null;
    chartData: Array<{
        dateTime: string | null;
        minHumidity: string | null;
        maxHumidity: string | null;
    }>;
};

export type LoadGraphData = {
    deviceId: string;
    deviceName: string;
    startDate: string;
    endDate: string;
    scaleX: string;
    scaleY: string;
    chartData: Array<{
        dateTime: string;
        highLoad: string;
    }>;
};

export type SensorListDetails = {
    deviceId: string;
    deviceName: string;
    channelData: Array<{
        channelNumber: number;
        channelName: string;
        isActive: boolean;
    }>;
};

export type DatetimeListDetails = {
    deviceId: string;
    deviceName: string;
    datesWithChannel: Array<{
        dateTime: string;
        channels: Array<{
            channelNumber: number;
            channelName: string;
        }>;
    }>;
};

export type SensorTrendDataDetails = {
    deviceId: string;
    deviceName: string;
    channelNumber: number;
    channelName: string;
    pdData: Array<{
        highPdi: string;
        pdiAlarmPoint: string;
        highQmax: string;
        qmaxAlarmPoint: string;
        highPps: string;
        highPpc: string;
    }>;
};

export type PDIGraphData = {
    deviceId: string;
    deviceName: string;
    startDate: string;
    endDate: string;
    scaleX: string;
    scaleY: string;
    channelsData: Array<{
        channelNumber: number;
        channelName: string;
        pdiChartData: Array<{
            isNewAlarm: boolean;
            dateTime: string;
            maxPdi: string;
            alarmCount: number;
            alarmPoint: string;
        }>;
    }>;
};

export type QmaxData = {
    deviceId: string | null;
    deviceName: string;
    startDate: string | null;
    endDate: string | null;
    scaleX: string | null;
    scaleY: string | null;
    channelsData: Array<{
        channelNumber: number;
        channelName: string;
        qmaxChartData: Array<{
            isNewAlarm: boolean;
            dateTime: string | null;
            maxQmax: string | null;
            alarmCount: number;
            alarmPoint: string | null;
        }>;
    }>;
};

export type PPCGraphData = {
    deviceId: string | null;
    deviceName: string;
    startDate: string | null;
    endDate: string | null;
    scaleX: string | null;
    scaleY: string | null;
    channelsData: Array<{
        channelNumber: number;
        channelName: string;
        ppcChartData: Array<{
            dateTime: string | null;
            maxPpc: string | null;
        }>;
    }>;
};

export type DeviceListData = {
    id: string;
    name: string;
    type: string | null;
    path: string[];
    iconClass: string;
    vendor: string | null;
    status: boolean;
    childDevicesExpected: boolean;
    deviceCount: number;
    isEditable: boolean;
    parentDeviceId: string;
    family?: string | null;
    areAllChildrenOnline: boolean | null;
    otaReleaseDetails: OTAReleaseDetails | null;
    otaUpgradeStatus: OtaUpgradeStatus | null;
    pxdbNavigation: boolean;
};
export type DevicePropertiesDetails = {
    id: string;
    name: string;
    siteId: string;
    siteName: string;
    type: string | null;
    family: string | null;
    vendor: string | null;
    serialNumber: string | null;
    syncedDate: string | null;
    iconClass: string;
    status: boolean;
    parentDeviceId?: string;
    isEditable?: boolean;
    devices: Device[];
    mac: string;
    role: string;
    isGatewaySyncTime?: boolean;
};

export type DeviceChannelData = {
    id: number | string;
    name: string | null;
    value: string | null;
    unit: string | null;
    type?: string | undefined;
    isEssential?: boolean;
};
export type DeviceConnectionData = { deviceId: string; status: boolean };

type DeviceRegistration = {
    name: string;
    siteId: string;
    modelType: string;
    deviceProfileId: string;
    validateInput?: boolean;
};

export type MeshDeviceRegistration = {
    name: string;
    siteId: string;
    deviceProfileId: string | undefined;
    // messageType: string;
    // telemetryData: Array<{
    //     uiAttribute: string;
    //     value: string;
    // }>;
    // dayLightSavingStartDate: string;
    // dayLightSavingEndDate: string;
    timeToLive: number;
};

export type PxdbDeviceRegistration = {
    name: string;
    siteId: string;
    deviceId: string; //GUID
    serial: string;
    deviceProfileID: string | undefined;
};
export type StarfishDeviceRegistration = {
    name: string;
    //deviceId: string;   //GUID
    siteId: string;
    deviceIp: string;
    serial: string;
    mac: string;
    deviceProfileId: string | undefined;
    deviceAddress: string;
    port?: number;
    unitId?: number;
    parentDeviceId?: string;
    pushRate: number;
    communicationType?: string;
    scanRate?: number;
    deviceType?: string;
    requestedFrom?: string;
};

export type StarfishChildDeviceRegistration = {
    name: string;
    siteId: string;
    deviceIp: string;
    serial: string;
    mac: string;
    deviceProfileId: string | undefined;
    deviceAddress?: number;
    port?: number;
    parentDeviceId?: string;
    communicationType?: string;
    scanRate: number;
    deviceType?: string;
    requestedFrom?: string;
    baudRate?: number;
    stopBits?: number;
    dataBits?: number;
    parity?: string;
    comPort?: string;
};

export type GatewayDeviceRegistration = DeviceRegistration & {
    serial: string;
    modbusAddress: string;
    imei: string;
    childDeviceTypes: string;
    blueprint: {
        id?: string;
        version?: string;
    };
    netmask: number;
    uart: uart;
};
export type uart = {
    baud: string;
    bits: number;
    flow: string;
    pair: string;
    stop: number;
    std: string;
    term: string;
};

export type ChildDeviceRegistrationData = DeviceRegistration & {
    gatewayDeviceId: string;
    ipAddress: string;
    parentModelType: string;
    unitId: number;
    ipPort: number;
};

export type SierraDeviceData = PromiseType<ReturnType<typeof DeviceManager.getSierraGatewayDeviceDetails>>;
export type StarfishDeviceData = PromiseType<ReturnType<typeof DeviceManager.getStarfishDeviceDetails>>;
export type SierraChildDeviceData = PromiseType<ReturnType<typeof DeviceManager.getSierraChildDeviceDetails>>;

export type MeshDeviceData = PromiseType<ReturnType<typeof DeviceManager.getMeshGatewayDeviceDetails>>;
export type GetPartNumberData = PromiseType<ReturnType<typeof DeviceManager.getPartNumberDetails>>;
export type GetSiteSubscriptionData = PromiseType<ReturnType<typeof DeviceManager.getSiteSubscriptionDetails>>;

export type StarfishChildDeviceData = PromiseType<ReturnType<typeof DeviceManager.getStarfishChildDeviceDetails>>;
export type ChildDevice = {
    key: string;
    value: string;
    type: string;
};

export type GatewayDevice = {
    childDevices: ChildDevice[];
    blueprint: {
        id: string;
        version: string;
    };
    childDeviceTypes: string;
    deviceProfileId: string;
};

export type MeshDeviceDetails = {
    profile: {
        siteId: string;
        publisherId: string;
        id: string;
        name: string;
        profileId: string;
        siteName: string;
    };
    dayLightSavingInfo: {
        dayLightSavingStart: string;
        dayLightSavingEnd: string;
    };
    telemetryData: {
        timeToLive: number;
        groupCount: number;
        deviceCount: number;
    };
};

export type GetPartNumberDetails = {
    siteId: string;

    results: Array<{
        partNumber: string;
        activationId: string;
        mappingHierarchy: string;
        startDate: string;
        expirationDate: string;
        remainingQuantity: number;
        fulFilledQuantity: number;
        totalQuantity: number;
        productLine: string;
        product: string;
        subscriptionStatus: number;
    }>;
};
export type GetSiteSubscriptionDetails = {
    isOtaEnabled: boolean;
    isFixtureIdentificationEnabled: boolean;
    groupingCount: number;
    isControlProfileEnabled: boolean;
    isGroupDimmingEnabled: boolean;
    isRtcDayLightSavingEnabled: boolean;
    isFirmwareLumenValueEnabled: boolean;
    isFixturePowerEnabled: boolean;
    isFixtureUsefulLifeEnabled: boolean;
    isCustomControlProfileEnabled: boolean;
    isHolidayEnabled: boolean;
    isDaylightHarvestingEnabled: boolean;
    isVirtualSensingEnabled: boolean;
    isOccupancySensingEnabled: boolean;
    cloudDataSync: {
        isClouldSyncEnabled: boolean;
        isTrendsEnabled: boolean;
        isRealtimeEnabled: boolean;
    };
    isThermalRolloverEnabled: boolean;
    sensorData: {
        isOccupancyEnabled: boolean;
        isLuxEnabled: boolean;
        isAmbientTempEnabled: boolean;
    };
    fixtureData: {
        isCurrentEnabled: boolean;
        isVoltageEnabled: boolean;
        isPowerEnabled: boolean;
        isEnergyEnabled: boolean;
        isInternalTemperatureEnabled: boolean;
    };
    isAlarmNotificationEnabled: boolean;
    isDashboardEnabled: boolean;
    subscriptionDetails: {
        partNumber: string;
        startDate: Date;
        expirationDate: Date;
        subscriptionStatus: number;
    };
};

export type MappingSiteIdActivationId = {
    siteId: string;
    activationId: string;
    isMapped: boolean;
};
export type OTAReleaseDetails = {
    metadata: string;
    version: string;
    signature: string;
    downloadUrl: string;
    relativeBlobUrl: string;
    createdDate: string;
    isDeleted: boolean;
    partitionKey: string;
    rowKey: string;
    timestamp: string;
    eTag: string;
};

export enum OtaUpgradeStatusType {
    Initiated,
    Downloaded,
    Unzipped,
    Copied,
    Success,
    Failed,
    Rejected,
}
export type OtaUpgradeStatus = {
    currentVersion: string;
    upgradeVersion: string;
    upgradeStatus: OtaUpgradeStatusType;
};

export enum MasterDataKey {
    Starfish = 'starfishchilddevices',
    StarfishBLG400 = 'blg400childdevices',
}
